"use client";

import { Card, CardContent } from "@/components/ui-kit/card";
import { Input } from "@/components/ui-kit/input";
import { Label } from "@/components/ui-kit/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-kit/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui-kit/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui-kit/tooltip";
import { Eye, EyeOff, Info, Plus, Trash2 } from "lucide-react";

import React from "react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { Button } from "@/components/ui-kit/button";
import { useTranslation } from 'react-i18next';

type AuthType =
  | "none"
  | "bearer"
  | "basic"
  | "api_token"
  | "oauth2"
  | "oauth2_client_credentials"
  | "oauth2_authorization_code"
  | "session"
  | "custom";

interface ToolAuthCardProps {
  errors?: Record<string, string>;
  excludeAuthTypes?: AuthType[];
  authType?: AuthType;
  showAuthTypeSelector?: boolean;
  showCard?: boolean;
}

export const ToolAuthCard = ({
  errors = {},
  excludeAuthTypes = [],
  authType: authTypeProp,
  showAuthTypeSelector = true,
  showCard = true,
}: ToolAuthCardProps) => {
  const { control, watch, setValue } = useFormContext();
  const { t } = useTranslation();

  const authType = authTypeProp || (watch("authType") as AuthType);
  const authLocation = watch("authLocation") as string;

  const [showBearerToken, setShowBearerToken] = React.useState(false);
  const [showBasicPassword, setShowBasicPassword] = React.useState(false);
  const [showClientSecret, setShowClientSecret] = React.useState(false);
  const [showApiTokenSecret, setShowApiTokenSecret] = React.useState(false);

  const {
    fields: oauth2Fields,
    append: appendOAuth2Field,
    remove: removeOAuth2Field,
  } = useFieldArray({
    control,
    name: "oauth2Headers",
  });

  React.useEffect(() => {
    if (authType === "bearer") setValue("authKeyPrefix", "Bearer");
    else if (authType === "basic") setValue("authKeyPrefix", "Basic");
    else if (authType === "api_token") setValue("authKeyPrefix", "Token");
  }, [authType, setValue]);

  React.useEffect(() => {
    if (authType !== "none" && authType !== "oauth2") {
      setValue("authKeyName", "Authorization");
    }
  }, [authType, setValue]);

  const content = (
    <div className="flex flex-col gap-6">
      {showAuthTypeSelector && (
        <div className="flex flex-col gap-4">
          <Label htmlFor="auth-method" className="mb-2">
            Authentication method
          </Label>
          <Controller
            name="authType"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="auth-method" className="w-full md:w-1/3">
                  <SelectValue placeholder={t('SELECT_AUTH_METHOD')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('AUTH_NONE')}</SelectItem>
                  {!excludeAuthTypes.includes("bearer") && (
                    <SelectItem value="bearer">{t('AUTH_BEARER')}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("basic") && (
                    <SelectItem value="basic">{t('AUTH_BASIC')}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("api_token") && (
                    <SelectItem value="api_token">{t('AUTH_API_TOKEN')}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("oauth2") && (
                    <SelectItem value="oauth2">{t('AUTH_OAUTH2')}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {authType !== "none" && (
        <>
          {authType === "bearer" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t('PROVIDE_AUTHENTICATION')}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Set the Header fields and values shared by all APIs within the tool here.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="bearer-key"
                      className={`mb-2 ${errors.authKeyName ? "text-destructive" : ""}`}
                    >
                      Key <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="authKeyName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="bearer-key"
                          placeholder="e.g. Authorization"
                          {...field}
                          className={errors.authKeyName ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyName && (
                      <span className="text-xs text-destructive">{errors.authKeyName}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="bearer-prefix"
                      className={`mb-2 ${errors.authKeyPrefix ? "text-destructive" : ""}`}
                    >
                      Prefix <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="authKeyPrefix"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="bearer-prefix"
                          placeholder="e.g., Bearer, Token"
                          {...field}
                          className={errors.authKeyPrefix ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyPrefix && (
                      <span className="text-xs text-destructive">{errors.authKeyPrefix}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="bearer-secret"
                      className={`mb-2 ${errors.apiToken ? "text-destructive" : ""}`}
                    >
                      Secret <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="apiToken"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <Input
                            id="bearer-secret"
                            type={showBearerToken ? "text" : "password"}
                            placeholder="Enter Secret"
                            {...field}
                            className={errors.apiToken ? "border-destructive" : ""}
                          />
                          <button
                            type="button"
                            onClick={() => setShowBearerToken(!showBearerToken)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showBearerToken ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    />
                    {errors.apiToken && (
                      <span className="text-xs text-destructive">{errors.apiToken}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {authType === "basic" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t('PROVIDE_AUTHENTICATION')}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Set the Header fields and values shared by all APIs within the tool here.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="basic-key"
                      className={`mb-2 ${errors.authKeyName ? "text-destructive" : ""}`}
                    >
                      Key <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="authKeyName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="basic-key"
                          placeholder="e.g. Authorization"
                          {...field}
                          className={errors.authKeyName ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyName && (
                      <span className="text-xs text-destructive">{errors.authKeyName}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="basic-prefix"
                      className={`mb-2 ${errors.authKeyPrefix ? "text-destructive" : ""}`}
                    >
                      Prefix <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="authKeyPrefix"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="basic-prefix"
                          placeholder="e.g., Bearer, Token"
                          {...field}
                          className={errors.authKeyPrefix ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyPrefix && (
                      <span className="text-xs text-destructive">{errors.authKeyPrefix}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="username"
                      className={`mb-2 ${errors.username ? "text-destructive" : ""}`}
                    >
                      Username <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="username"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="username"
                          placeholder="Enter username"
                          {...field}
                          className={errors.username ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.username && (
                      <span className="text-xs text-destructive">{errors.username}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="password"
                      className={`mb-2 ${errors.password ? "text-destructive" : ""}`}
                    >
                      Password <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <Input
                            id="password"
                            type={showBasicPassword ? "text" : "password"}
                            placeholder="Enter password"
                            {...field}
                            className={errors.password ? "border-destructive" : ""}
                          />
                          <button
                            type="button"
                            onClick={() => setShowBasicPassword(!showBasicPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showBasicPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    />
                    {errors.password && (
                      <span className="text-xs text-destructive">{errors.password}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {authType === "api_token" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t('PROVIDE_AUTHENTICATION')}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Set the Header fields and values shared by all APIs within the tool here.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="flex flex-col gap-4">
                  <Label>Location</Label>
                  <Controller
                    name="authLocation"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup value={field.value} onValueChange={field.onChange}>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="header" id="api-header" />
                            <Label htmlFor="api-header" className="font-normal">
                              Header
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="query" id="api-query" />
                            <Label htmlFor="api-query" className="font-normal">
                              Query
                            </Label>
                          </div>
                        </div>
                      </RadioGroup>
                    )}
                  />
                </div>

                <div
                  className={`grid ${authLocation === "query" ? "grid-cols-1" : "grid-cols-2"} gap-4`}
                >
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="api-token-key"
                      className={`mb-2 ${errors.authKeyName ? "text-destructive" : ""}`}
                    >
                      Key <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="authKeyName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="api-token-key"
                          placeholder="e.g. Authorization"
                          {...field}
                          className={errors.authKeyName ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyName && (
                      <span className="text-xs text-destructive">{errors.authKeyName}</span>
                    )}
                  </div>

                  {authLocation !== "query" && (
                    <div className="flex flex-col gap-2">
                      <Label
                        htmlFor="api-token-prefix"
                        className={`mb-2 ${errors.authKeyPrefix ? "text-destructive" : ""}`}
                      >
                        Prefix <span className="text-destructive">*</span>
                      </Label>
                      <Controller
                        name="authKeyPrefix"
                        control={control}
                        render={({ field }) => (
                          <Input
                            id="api-token-prefix"
                            placeholder="e.g., Bearer, Token"
                            {...field}
                            className={errors.authKeyPrefix ? "border-destructive" : ""}
                          />
                        )}
                      />
                      {errors.authKeyPrefix && (
                        <span className="text-xs text-destructive">{errors.authKeyPrefix}</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="api-token-value"
                      className={`mb-2 ${errors.apiToken ? "text-destructive" : ""}`}
                    >
                      Secret <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="apiToken"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <Input
                            id="api-token-value"
                            type={showApiTokenSecret ? "text" : "password"}
                            placeholder="Enter Secret"
                            {...field}
                            className={errors.apiToken ? "border-destructive" : ""}
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiTokenSecret(!showApiTokenSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showApiTokenSecret ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    />
                    {errors.apiToken && (
                      <span className="text-xs text-destructive">{errors.apiToken}</span>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                      <Label htmlFor="oauth2Headers" className="mb-2 font-medium">
                        Headers
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Configure headers required for the OAuth2 token request.
                      </p>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2 font-medium">
                        <Label>Key</Label>
                        <Label>Value</Label>
                      </div>

                      {oauth2Fields.map((field, index) => (
                        <div key={field.id} className="flex items-center gap-4">
                          <Controller
                            control={control}
                            name={`oauth2Headers.${index}.key`}
                            render={({ field }) => (
                              <Input placeholder="Header Key" {...field} className="flex-1" />
                            )}
                          />
                          <Controller
                            control={control}
                            name={`oauth2Headers.${index}.value`}
                            render={({ field }) => (
                              <Input placeholder="Header Value" {...field} className="flex-1" />
                            )}
                          />
                          {oauth2Fields.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeOAuth2Field(index)}
                              className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          appendOAuth2Field({ id: Date.now().toString(), key: "", value: "" })
                        }
                        className="w-fit gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {authType === "oauth2" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t('AUTH_CONFIGURATION')}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('CONFIGURE_OAUTH2_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="client-id"
                      className={`mb-2 ${errors.clientId ? "text-destructive" : ""}`}
                    >
                      Client ID <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="clientId"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="client-id"
                          placeholder="Enter client ID"
                          {...field}
                          className={errors.clientId ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.clientId && (
                      <span className="text-xs text-destructive">{errors.clientId}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="client-secret"
                      className={`mb-2 ${errors.clientSecret ? "text-destructive" : ""}`}
                    >
                      Client Secret <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="clientSecret"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <Input
                            id="client-secret"
                            type={showClientSecret ? "text" : "password"}
                            placeholder="Enter client secret"
                            {...field}
                            className={errors.clientSecret ? "border-destructive" : ""}
                          />
                          <button
                            type="button"
                            onClick={() => setShowClientSecret(!showClientSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showClientSecret ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    />
                    {errors.clientSecret && (
                      <span className="text-xs text-destructive">{errors.clientSecret}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="token-url"
                      className={`mb-2 ${errors.tokenUrl ? "text-destructive" : ""}`}
                    >
                      Token URL <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="tokenUrl"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="token-url"
                          placeholder="https://example.com/oauth/token"
                          {...field}
                          className={errors.tokenUrl ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.tokenUrl && (
                      <span className="text-xs text-destructive">{errors.tokenUrl}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grant-type" className="mb-2">
                      Grant Type <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      name="grantType"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="grant-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="client_credentials">{t('CLIENT_CREDENTIALS')}</SelectItem>
                            {/* <SelectItem value="authorization_code">Authorization Code</SelectItem>
                            <SelectItem value="refresh_token">Refresh Token</SelectItem> */}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="scopes" className="mb-2">
                    Scopes (comma separated)
                  </Label>
                  <Controller
                    name="scopes"
                    control={control}
                    render={({ field }) => (
                      <Input id="scopes" placeholder="read, write, admin" {...field} />
                    )}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="oauth2Headers" className="mb-2 flex flex-row gap-2">
                    Headers
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('OAUTH2_HEADERS_TOOLTIP')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 font-medium">
                    <Label>Key</Label>
                    <Label>Value</Label>
                  </div>

                  {oauth2Fields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-4">
                      <Controller
                        control={control}
                        name={`oauth2Headers.${index}.key`}
                        render={({ field }) => (
                          <Input placeholder="Header Key" {...field} className="flex-1" />
                        )}
                      />
                      <Controller
                        control={control}
                        name={`oauth2Headers.${index}.value`}
                        render={({ field }) => (
                          <Input placeholder="Enter value" {...field} className="flex-1" />
                        )}
                      />
                      {oauth2Fields.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOAuth2Field(index)}
                          className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      appendOAuth2Field({ id: Date.now().toString(), key: "", value: "" })
                    }
                    className="w-fit gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (showCard) {
    return (
      <Card className="tool-auth-card">
        <CardContent>{content}</CardContent>
      </Card>
    );
  }

  return content;
};
